import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Foreground push banner + generic transient messages (feature #8). */
export interface ToastMessage {
  id: number;
  title?: string;
  body: string;
  onClick?: () => void;
}

interface ToastApi {
  show: (msg: Omit<ToastMessage, 'id'>) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const show = useCallback((msg: Omit<ToastMessage, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...msg, id }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-2 z-50 flex flex-col items-center gap-2 px-3">
        {toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              t.onClick?.();
              setToasts((prev) => prev.filter((x) => x.id !== t.id));
            }}
            className="pointer-events-auto w-full max-w-sm rounded-xl bg-slate-900/95 px-4 py-3 text-left text-white shadow-lg"
          >
            {t.title ? <p className="text-sm font-semibold">{t.title}</p> : null}
            <p className="text-sm text-slate-200">{t.body}</p>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
